/**
 * # Logo
 *
 * React component that renders a logo based on the specified type.
 *
 * ## Props
 * - `color` (optional): Specifies the color of the logo. Can be one of 'default', 'white', or 'blue'. Defaults to 'default'.
 * - `display` (optional): Specifies the type of logo to render. Can be one of 'mark', 'text', or 'combined'. Defaults to 'combined'.
 * - `light` (optional): Specifies whether the logo should be rendered in light mode. Defaults to false.
 * - `width` (optional): Specifies the width of the logo. Defaults to '159px'.
 *
 * ## Usage
 *
 * ```tsx
 * import Logo from './Logo.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <div>
 *       <Logo type="mark" light width="200px" />
 *     </div>
 *   );
 * };
 * ```
 */

import React from 'react';

import LogoText from './assets/LogoType.react';
import LogoCombined from './assets/LogoCombined.react';
import LogoMark from './assets/LogoMark.react';

import './logo.scss';

interface Props {
  display?: 'face' | 'text' | 'combined';
  color?: 'white' | 'blue';
  width?: string;
  height?: string;
  className?: string;
}

export const Logo = ({
  display = 'combined',
  color = 'blue',
  width = '159px',
  height = 'auto',
  className,
  ...rest
}: Props) => {
  const LogoVariants = {
    face: LogoMark,
    text: LogoText,
    combined: LogoCombined
  };

  const LogoVariant = LogoVariants[display];

  return (
    <div
      className={`${className ? className : ''} a-logo a-logo--${color}`}
      style={{width, height}}
      {...rest}
    >
      <LogoVariant />
    </div>
  );
};

Logo.displayName = 'Logo';

export default Logo;
